import PortalLayout from '@/components/layout/portal/PortalLayout';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { useDisclosure, UseDisclosureReturn } from '@/utils/hooks/useDisclosure';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/common/Button';
import PortalCurrencyIcon from '@/components/common/PortalCurrencyIcon';
import { StatusPill, StatusTypes } from '@/components/common/StatusPill';
import DeactivateBankWalletModal from '@/components/modal/DeactivateBankWalletModal';
import PortalBankDetailsModal from '@/components/modal/PortalBankDetailsModal';
import PortalWhitelistModal from '@/components/modal/PortalWhitelistModal';
import PortalBankWalletStatusCard from '@/components/portal-modules/PortalBankWalletStatusCard';
import PortalRecentTransactionsTable from '@/components/portal-modules/PortalRecentTransactionsTable';
import { formatToDate, getOperationalCurrentTradeDate } from '@/helpers/dateHelper';
import { selectDismissedIds, selectDismissedWhitelist } from '@/state/reducers/portalPlatformSlice';
import { selectTablePagination } from '@/state/reducers/tableSettingSlice';
import { FlattenPortalRequest, PortalRequestResponse } from '@/types/sso';
import Unauthorized401 from '@/Unauthorized401';
import { getCurrency } from '@/utils/hooks/useCurrency';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalAuthentication from '@/utils/hooks/usePortalAuthentication';
import { fetchPortalAPI, usePortalSWR, useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import { subYears } from 'date-fns';
import { MdDeleteOutline, MdOutlineAccountBalance, MdOutlineVisibility, MdRefresh } from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';

const cardHeight = 99.1;
const currentTradeDate = getOperationalCurrentTradeDate();

function PortalBankAccounts() {
    usePortalAuthentication();

    const { jwt, bankInfoApi, foundEntity, hasPermission, onFetchJWT } = useSso();

    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const pagination = useAppSelector(selectTablePagination);
    const dismissedIds = useAppSelector(selectDismissedIds);
    const dismissedWhitelistRequests = useAppSelector(selectDismissedWhitelist);

    const lang = useLanguage();
    const whitelistDisclosure = useDisclosure(false);
    const bankAccountDiscloure = useDisclosure(false);
    const deactivateBankDisclosure = useDisclosure(false);

    const [offset, setOffset] = useState<number>(0);
    const [selectedRequest, setSelectedRequest] = useState<FlattenPortalRequest>();

    const bankRequestPayload = useMemo(
        () => ({
            startDate: formatToDate(subYears(currentTradeDate, 1)),
            endDate: formatToDate(currentTradeDate),
            entityId: foundEntity?.entityId.toString(),
            limit: 999,
            offset: offset.toString(),
            requestType: 'WHITELIST_BANK_INFO,REMOVE_BANK_INFO'
        }),
        [foundEntity?.entityId, pagination.pageSize, offset]
    );

    const bankWhitelistApi = usePortalSWR<PortalRequestResponse>('/api/requests/list-whitelist-banks', () =>
        fetchPortalAPI(onFetchJWT, '/api/requests/list', jwt, bankRequestPayload, 'POST')
    );

    const { data: trueBanksData = [] } = bankInfoApi;
    const { data: requestBanksData = { data: [], total: 0 } } = bankWhitelistApi;

    console.log(trueBanksData);

    const isVloading = useMemo(() => {
        return (
            bankInfoApi.isLoading ||
            bankInfoApi.isValidating ||
            bankWhitelistApi.isLoading ||
            bankWhitelistApi.isValidating
        );
    }, [bankInfoApi, bankWhitelistApi]);

    const bankAccounts = useMemo(() => {
        if (trueBanksData && requestBanksData) {
            const assignedTrueBanksData = trueBanksData.map((bank) => {
                const matchingRequests = requestBanksData.data.filter((request) => {
                    return (
                        request.details.bankInfoId === bank.bankInfoId ||
                        (request.details.friendlyName === bank.friendlyName &&
                            request.details.bankName === bank.bankName &&
                            request.details.accountNumber === bank.accountNumber &&
                            request.details.ibanCode === bank.ibanCode &&
                            request.details.baseCurrencyId === bank.baseCurrencyId)
                    );
                });

                return {
                    ...bank,
                    requests: matchingRequests
                };
            });

            const combinedBanksData = [
                ...convertTrueBanksData(
                    assignedTrueBanksData.filter((b) => !dismissedIds.banks.includes(b.bankInfoId))
                ),
                ...convertRequestBanksData(
                    requestBanksData.data.filter(
                        (r) =>
                            ['INITIATED', 'CANCELED', 'REJECTED'].includes(r.requestStatus) &&
                            !dismissedWhitelistRequests.banks.includes(r.id)
                    )
                )
            ];

            const banksToBeRemoved = requestBanksData.data
                .filter(
                    (r) => r.requestType === 'REMOVE_BANK_INFO' && ['INITIATED', 'APPROVED'].includes(r.requestStatus)
                )
                .map((r) => r.details.bankInfoId);

            combinedBanksData.sort((a, b) => {
                if (a.details.active && b.details.active) return -1;
                if (b.details.active && a.details.active) return 1;
                return 0;
            });

            return combinedBanksData.filter(
                (item) =>
                    !(banksToBeRemoved.includes(item.bankInfoId) || banksToBeRemoved.includes(item.details.bankInfoId))
            );
        }
        return [];
    }, [trueBanksData, requestBanksData, dismissedIds, dismissedWhitelistRequests]);

    const requestPayload = useMemo(
        () => ({
            startDate: formatToDate(subYears(currentTradeDate, 1)),
            endDate: formatToDate(currentTradeDate),
            entityId: foundEntity?.entityId.toString(),
            limit: 999,
            offset: offset.toString(),
            requestType: 'DEPOSIT,WITHDRAWAL,INTERNAL_TRANSFER'
        }),
        [foundEntity?.entityId, pagination.pageSize, offset]
    );

    const recentTransactionsApi = usePortalSWR<PortalRequestResponse>('/api/requests/list-recent-transactions', () =>
        fetchPortalAPI(onFetchJWT, '/api/requests/list', jwt, requestPayload, 'POST')
    );

    const { data: recentTransactionsData } = recentTransactionsApi;

    useEffect(() => {
        bankInfoApi.mutate();
        bankWhitelistApi.mutate();
        recentTransactionsApi.mutate();
    }, [currentAccount]);

    const isAuthorized = hasPermission('REQUEST_WHITELIST');

    return credentials ? (
        <PortalLayout isAuthorized={isAuthorized}>
            {isAuthorized && (
                <Fragment>
                    <div className="flex flex-col w-full gap-2 sm:gap-4 p-2 sm:p-6 text-brand-text mt-16 lg:mt-0">
                        <div className="flex flex-wrap md:h-[34px] gap-2 w-full justify-between">
                            <div className="font-bold text-xl">{lang.navigation['bank accounts']}</div>
                            <Button
                                type="submit"
                                className="h-fit w-fit !gap-2 text-xs"
                                onClick={whitelistDisclosure[1].open}>
                                <MdOutlineAccountBalance className="w-4 h-4" />
                                {lang.portal.addBankAccount}
                            </Button>
                        </div>
                        <div className="flex flex-col gap-4 bg-brand-background-dark border border-brand-neutral-700 text-brand-text rounded-md">
                            <div className="flex items-center justify-between w-full p-4 sm:p-6 pt-4 !pb-0">
                                <div className="">{lang.portal.whitelistedBankAccounts}</div>
                                <div className="flex items-center gap-2 justify-end">
                                    <div
                                        className={cn(
                                            'border border-brand-neutral-700 rounded-md p-1.5 cursor-pointer',
                                            { 'hover:bg-brand-primary': !isVloading }
                                        )}
                                        onClick={() => {
                                            if (!isVloading) {
                                                bankInfoApi.mutate();
                                                bankWhitelistApi.mutate();
                                            }
                                        }}>
                                        <MdRefresh
                                            className={cn('w-4 h-4', {
                                                'animate-spin text-brand-neutral-400': isVloading
                                            })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap w-full gap-2 sm:gap-4 max-h-[380px] overflow-y-auto p-4 sm:p-6 pb-4 !pt-0">
                                {isVloading ? (
                                    <div className="flex flex-col w-full gap-4">
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <div className="flex gap-2">
                                            <Skeleton className="!rounded-full" height={12} width={12} />
                                            <Skeleton className="!rounded-full" height={12} width={12} />
                                            <Skeleton className="!rounded-full" height={12} width={12} />
                                        </div>
                                    </div>
                                ) : (
                                    <Fragment>
                                        {bankAccounts?.length > 0 ? (
                                            <Fragment>
                                                {bankAccounts?.map((bank, index) => {
                                                    const flattenBankDetails = {
                                                        ...bank,
                                                        ...bank.details
                                                    };

                                                    return (
                                                        <BankCard
                                                            key={index}
                                                            bankDetails={flattenBankDetails}
                                                            bankAccountDiscloure={bankAccountDiscloure}
                                                            deactivateBankDisclosure={deactivateBankDisclosure}
                                                            onClick={() => setSelectedRequest(flattenBankDetails)}
                                                        />
                                                    );
                                                })}
                                            </Fragment>
                                        ) : (
                                            <div className="text-xs text-brand-text">
                                                {lang.portal.noBankAccountsWhitelistedYet}
                                            </div>
                                        )}
                                    </Fragment>
                                )}
                            </div>
                        </div>

                        <PortalBankWalletStatusCard
                            type="Bank"
                            requestData={requestBanksData ?? { data: [], total: 0 }}
                        />
                        <PortalRecentTransactionsTable
                            mode="bank"
                            data={recentTransactionsData}
                            filters={undefined}
                            setFilters={() => {}}
                            defaultFilters={undefined}
                            setOffset={setOffset}
                            isLoading={recentTransactionsApi.isLoading || recentTransactionsApi.isValidating}
                            mutate={recentTransactionsApi.mutate}
                        />
                    </div>
                    <PortalWhitelistModal
                        mode="bank"
                        opened={whitelistDisclosure[0]}
                        handlers={whitelistDisclosure[1]}
                        onRefresh={() => {
                            bankInfoApi.mutate();
                            bankWhitelistApi.mutate();
                        }}
                    />
                    <PortalBankDetailsModal
                        handlers={bankAccountDiscloure[1]}
                        opened={bankAccountDiscloure[0]}
                        onClose={bankAccountDiscloure[1].close}
                        selectedBankAccount={selectedRequest}
                    />
                    <DeactivateBankWalletModal
                        mode="bank"
                        opened={deactivateBankDisclosure[0]}
                        handlers={deactivateBankDisclosure[1]}
                        selectedBankAccount={selectedRequest}
                        onRefresh={() => {
                            bankInfoApi.mutate();
                            bankWhitelistApi.mutate();
                        }}
                    />
                </Fragment>
            )}
            {!isAuthorized && <Unauthorized401 />}
        </PortalLayout>
    ) : null;
}

export default PortalBankAccounts;

export interface BankWalletCardProps {
    bankDetails: FlattenPortalRequest;
    bankAccountDiscloure: UseDisclosureReturn;
    deactivateBankDisclosure: UseDisclosureReturn;
    onClick: () => void;
}

export const BankCard = (props: BankWalletCardProps) => {
    const { bankDetails, bankAccountDiscloure, deactivateBankDisclosure, onClick } = props;

    const { currenciesApi } = useSso();

    const status = useMemo(() => bankDetails?.requestStatus?.toLowerCase() as StatusTypes, [bankDetails]);

    const maskedAccountNumber = useMemo(() => {
        if (bankDetails.accountNumber) return bankDetails.accountNumber.replace(/.(?=.{4})/g, '*');
        else if (bankDetails.ibanCode) return bankDetails.ibanCode.replace(/.(?=.{4})/g, '*');
        return 'N/A';
    }, [bankDetails]);

    const statusText = useMemo(() => {
        if (!bankDetails.active) return 'deactivated';

        if (status === 'initiated') {
            if (bankDetails.requestType === 'REMOVE_BANK_INFO') return 'pending removal';
            else if (bankDetails.requestType === 'WHITELIST_BANK_INFO') return 'pending approval';
        }
        if (status === 'update') return 'pending update';
        if (status === undefined) return 'approved';
        return status;
    }, [status, bankDetails.active]);

    const currencies = useMemo(() => {
        if (bankDetails.baseCurrencyName) {
            const { show } = getCurrency(bankDetails.baseCurrencyName || '');
            return show;
        } else if (bankDetails.baseCurrencyId) {
            const currency = currenciesApi?.data?.find((c) => c.id === bankDetails.baseCurrencyId);
            const { show } = getCurrency(currency?.digitalAssetName || currency?.name || '');
            return show;
        }
        return '';
    }, [bankDetails.baseCurrencyName, currenciesApi]);

    const deleteTitle = useMemo(() => {
        if (status === 'initiated') return 'Cancel Request';
        if (status === 'update') return 'Cancel Update Request';
        if (status === 'rejected') return 'Dismiss Bank Account';
        return 'Deactivate Bank Account';
    }, [status]);

    const statusType = useMemo(() => {
        if (!bankDetails.active) return 'deactivated';
        if (status === undefined) return 'approved';
        return status;
    }, [status, bankDetails.active]);

    return (
        <div className="bg-brand-background-dark p-4 border border-brand-neutral-700 hover:border-brand-neutral-600 rounded-md w-full">
            <div className="flex gap-4">
                <div className={cn('flex flex-col gap-2 text-sm text-brand-text')}>
                    <div className={cn('flex flex-wrap gap-2 text-sm')}>
                        {bankDetails.bankName}
                        <StatusPill statusText={statusText} statusType={statusType} />
                    </div>
                    <div className="text-brand-neutral-400 text-2xs sm:text-xs leading-normal">
                        {bankDetails.friendlyName} {maskedAccountNumber}
                    </div>
                    <div className="flex items-center gap-1 text-brand-neutral-400 text-2xs sm:text-xs leading-normal">
                        <PortalCurrencyIcon show={currencies} className="!w-3 !h-3" />
                        {currencies}
                    </div>
                </div>
                <div className="flex-1 justify-end flex items-center gap-2">
                    <div className="flex items-center justify-end gap-2 min-w-12">
                        <button
                            title={deleteTitle}
                            onClick={() => {
                                onClick();
                                deactivateBankDisclosure[1].open();
                            }}
                            className="text-brand-red hover:text-brand-red-light">
                            <MdDeleteOutline className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => {
                                onClick();
                                bankAccountDiscloure[1].open();
                            }}
                            className="text-brand-text hover:text-brand-primary-light">
                            <MdOutlineVisibility className="w-5 h-5" />
                        </button>
                        {/* <button onClick={onClick} className="text-brand-text hover:text-brand-primary-light">
                            <MdOutlineOpenInNew className="w-5 h-5" />
                        </button> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

const convertTrueBanksData = (data: any) => {
    if (data) {
        return data.map((item: any) => ({
            ...item,
            details: {
                ...item,
                active: item.active || false
            }
        }));
    }
    return [];
};

const convertRequestBanksData = (data: any) => {
    if (data) {
        return data.map((item: any) => ({
            ...item,
            details: {
                ...item.details,
                active: true
            }
        }));
    }
    return [];
};
